import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios, { CancelTokenSource } from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Typography
} from '@mui/material';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { useApi } from '@bumble/app/hooks/use-api';
import '../pcap-analyzer/AnalyzerModal.css';
import DialogConfirm from '../DialogConfirm';

interface DocumentUploaderProps {
  open: boolean;
  handleClose: () => void;
  owner?: any;
}

type UploadStatus = 'uploading' | 'processing' | 'done' | 'error';

interface Upload {
  key: string;
  filename: string;
  progress: number;
  status: UploadStatus;
}

const MAX_FILE_SIZE = 10737418240;

export const DocumentUploader = ({ open, handleClose, owner }: DocumentUploaderProps) => {
  const [showConfirmCloseDialog, setShowConfirmCloseDialog] = useState<boolean>(false);
  const [uploads, setUploads] = useState<Upload[]>([]);

  const api = useApi();

  // Keeps one cancel token per in-flight upload so closing the dialog can abort
  // all of them, not just the most recent.
  const cancelSources = useRef<Map<string, CancelTokenSource>>(new Map());

  const isUploading = uploads.some(
    (u) => u.status === 'uploading' || u.status === 'processing'
  );

  // Start each dialog session with a clean slate.
  useEffect(() => {
    if (open) {
      setUploads([]);
      cancelSources.current.clear();
    }
  }, [open]);

  const handleError = (err: any) => {
    if (err.response) {
      const status = err.response.status;
      if (status === 400) {
        toast.error(err.response.data.detail);
      } else if (status === 413) {
        toast.error('Error: Chosen file is too large to upload.');
      } else if (status === 500) {
        toast.error(err.response.data.detail);
      } else {
        toast.error(`Error ${status}: ${err.message}`);
      }
    } else if (err.code && err.message) {
      err.code !== 'ERR_CANCELED' && toast.error(`Error ${err.code}: ${err.message}`);
    } else {
      toast.error(`Unknown error`);
    }
  };

  const updateUpload = (key: string, changes: Partial<Upload>) => {
    setUploads((prev) => prev.map((u) => (u.key === key ? { ...u, ...changes } : u)));
  };

  const uploadFile = useCallback(
    async (file: File) => {
      // Name alone isn't unique enough — the same file can be picked twice.
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      const source = axios.CancelToken.source();
      cancelSources.current.set(key, source);

      setUploads((prev) => [
        ...prev,
        { key, filename: file.name, progress: 0, status: 'uploading' },
      ]);

      try {
        // Each file gets its own Document row and pre-signed URL.
        const { data: uploadUrl } = await api.getDocumentS3UploadUrlApiV1GetDocumentS3UploadUrlGet({
          filename: file.name,
          node_id: owner?.value,
        });

        await axios.put(uploadUrl.s3_url, file, {
          headers: { 'Content-Type': '' },
          cancelToken: source.token,
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            const percentage = total ? Math.floor((loaded * 100) / total) : 0;
            updateUpload(key, { progress: percentage });
          },
        });

        // A document stays hidden from My Documents until it is reported
        // uploaded, so this call is what actually makes it appear.
        updateUpload(key, { status: 'processing' });
        await api.documentUploadedApiV1DocumentUploadedPost({
          document_id: uploadUrl.document_id,
        });
        updateUpload(key, { status: 'done', progress: 100 });
      } catch (error) {
        handleError(error);
        updateUpload(key, { status: 'error' });
      } finally {
        cancelSources.current.delete(key);
      }
    },
    [api, owner]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    disabled: isUploading,
    validator: (file) => {
      if (file.size > MAX_FILE_SIZE) {
        return {
          code: 'file-too-large',
          message: 'File size exceeds 10GB limit.',
        };
      }
      return null;
    },
    onDrop: (acceptedFiles, fileRejections) => {
      // Oversized files are skipped individually — the rest of the batch still
      // uploads, and the dialog stays open.
      fileRejections.forEach(({ file }) => {
        toast.error(
          `${file.name} is larger than 10GB and was skipped. You may filter this pcap file with a wireshark to reduce its size and try again.`,
          { autoClose: false }
        );
      });
      acceptedFiles.forEach(uploadFile);
    },
  });

  // Close once everything has landed. If any file failed, stay open so the
  // error is visible and the user can retry.
  useEffect(() => {
    if (uploads.length > 0 && uploads.every((u) => u.status === 'done')) {
      handleClose();
    }
  }, [uploads, handleClose]);

  const handleModalClose = () => {
    // Nothing in flight means nothing to lose — skip the confirmation.
    if (!isUploading) {
      closeModal();
      return;
    }
    setShowConfirmCloseDialog(true);
  };

  const closeModal = () => {
    cancelSources.current.forEach((source) => source.cancel());
    cancelSources.current.clear();
    handleClose();
  };

  const statusLabel = (upload: Upload) => {
    if (upload.status === 'error') return ' — upload failed';
    if (upload.status === 'processing') return ' — processing...';
    if (upload.status === 'done') return ' — done';
    return '';
  };

  const doneCount = uploads.filter((u) => u.status === 'done').length;

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        onClose={handleModalClose}
        aria-labelledby="standard-dialog"
        aria-describedby="standard-dialog-description"
      >
        <DialogTitle
          id="standard-dialog"
          sx={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}
          color="primary"
        >
          Upload documents
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 5, display: 'flex', gap: 20 }}>
            <Typography color="primary">
              Owner: {owner?.label}
            </Typography>
          </Box>
          <section className="container">
            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <p>
                {isDragActive
                  ? 'Drop the files here...'
                  : "Drag 'n' drop document files here, or click to select files."}
              </p>
            </div>
          </section>

          {uploads.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Uploading {doneCount} of {uploads.length} file
                {uploads.length === 1 ? '' : 's'}
              </Typography>
              <List dense disablePadding>
                {uploads.map((upload) => (
                  <ListItem key={upload.key} disableGutters>
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body2"
                        color={upload.status === 'error' ? 'error' : 'text.primary'}
                      >
                        {upload.filename}
                        {statusLabel(upload)}
                      </Typography>
                      {upload.status !== 'error' && (
                        <LinearProgress
                          variant={
                            upload.status === 'processing' ? 'indeterminate' : 'determinate'
                          }
                          value={upload.progress}
                          color={upload.status === 'done' ? 'success' : 'info'}
                          sx={{ height: 8, mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <IconButton
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
            }}
            onClick={handleModalClose}
          >
            <HighlightOffOutlinedIcon fontSize="small" />
          </IconButton>
        </DialogActions>
      </Dialog>
      {showConfirmCloseDialog && (
        <DialogConfirm
          open={showConfirmCloseDialog}
          handleClose={() => setShowConfirmCloseDialog(false)}
          handleConfirm={closeModal}
          title="Close document upload modal?"
          description="Uploads still in progress will be cancelled. Are you certain you wish to proceed with closing document upload modal?"
        />
      )}
    </>
  );
};
